import '@components/Common/CommonStyling.scss';
import './DeckIndex.scss';

import { RowClickedEvent } from '@amzn/gft-ag-grid-enterprise';
import { GraphQLResult } from '@aws-amplify/api-graphql';
import { AurigaGrid } from '@components/Common/AurigaGrid/AurigaGrid';
import { globalGridDefaults } from '@constants/agGrid';
import { useDeckContext } from '@context/DeckContext';
import useRecordLoadComplete from '@hooks/useRecordLoadComplete';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { Deck } from '@services/api/deckManagement/graphql/schema/API';
import { entityKeyAsId } from '@utils/deckHelpers';
import { useNavigate } from 'react-router-dom';

const DeckIndex = () => {
  const navigate = useNavigate();
  const { availableDecks: decks, isLoading, isError, error, setCurrentDeck } = useDeckContext();
  useRecordLoadComplete(!isLoading && !isError);

  // Simple single-column definition to match mock
  const columnDefs = [
    {
      field: 'deck_name' as keyof Deck,
      headerName: 'Deck Name',
      flex: 1,
    },
  ];

  const defaultColDef = {
    sortable: false,
    filter: false,
    resizable: false,
  };

  /**
   * Handle deck selection navigation from the table
   * @param params - The row clicked event
   */
  const handleDeckSelect = (params: RowClickedEvent<Deck>) => {
    if (params.data) {
      // Use the pk directly as the selected deck ID
      const deckPk = params.data.pk;
      // Extract the deck ID from the primary key for the URL
      const urlDeckId = entityKeyAsId(deckPk);
      // Set the selected deck in the DeckContext
      setCurrentDeck(urlDeckId);

      // Navigate to the TOC page
      navigate(`/deck-preparation/decks/${urlDeckId}/toc`);
    }
  };

  // Render content based on loading and error states
  const renderContent = () => {
    if (isLoading) {
      return (
        <Box className='loading-container'>
          <CircularProgress />
        </Box>
      );
    }

    if (isError) {
      // Surface first error in view body, in addition to default toasts that get surfaced
      const apiError = error as unknown as GraphQLResult;
      return (
        <Box className='error-message'>
          <Typography color='error'>
            Error loading decks: {apiError && apiError?.errors ? apiError?.errors[0]?.message : 'Unknown error'}
          </Typography>
        </Box>
      );
    }

    return (
      <Box className='grid-container table-container'>
        <AurigaGrid
          {...globalGridDefaults}
          rowData={decks}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          rowClass='selectable-row'
          onRowClicked={handleDeckSelect}
        />
      </Box>
    );
  };

  return (
    <Box className='deck-index'>
      <Box className='header'>
        <Typography variant='h6'>Active Decks</Typography>
      </Box>
      {renderContent()}
    </Box>
  );
};

export default DeckIndex;
